import {
  ReactNode, useCallback, useContext, useEffect, useState,
} from 'react';

import {
  Box,
  Button,
  Grid,
  Menu,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

// Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars,
  faPenToSquare,
  faX,
} from '@fortawesome/free-solid-svg-icons';

import { Link, useNavigate } from 'react-router-dom';
import {
  Undo,
  SaveOutlined,
  ReplayRounded,
  SettingsRounded,
} from '@mui/icons-material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useUserContext } from '../contexts/UserContext';
import { LayoutContext } from '../contexts/LayoutContext';
import { PagesContext, PageSetting } from '../contexts/PagesContext';
import BlockData from '../models/BlockData';
import { MyMenu } from './PrivateLayout/MyMenu';
import PageSettingsModal from '../components/Modals/PageSettingsModal/PageSettingsModal';
import { sanitizePageSettingsForm } from '../hooks/SanitizePageSettingsForm';
import ModalComponent from '../components/ModalComponent';

type Props = {
  pageId: string | null;
  isEditing: boolean;
  isSaving: boolean;
  canUndo: boolean;
  blockItems: BlockData[];
  initialBlockItems: BlockData[];
  settings: PageSetting;
  handleSettingSave: (settings: PageSetting, cb?: () => void, ccb?: (error: any) => void) => void;
  handleUndo: () => void;
  handleEdit: (value: boolean) => void;
  handleSave: (value: boolean) => void;
  renderHtmlExportButton: () => ReactNode
  renderSmartPhoneButton: () => ReactNode;
};

const MainPageLayout = ({
  pageId,
  handleUndo,
  handleEdit,
  handleSave,
  isEditing,
  isSaving,
  canUndo,
  blockItems,
  initialBlockItems,
  settings,
  handleSettingSave,
  renderHtmlExportButton,
  renderSmartPhoneButton,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const { user } = useUserContext();
  const { removeLayout } = useContext(LayoutContext);
  const { Update, contextErrors, updatePageLoading } = useContext(PagesContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [openPageSettingModal, setOpenPageSettingModal] = useState(false);
  const navigate = useNavigate();

  // VALIDATION RELATED STATES
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const [localInitialBlockItems, setLocalInitialBlockItems] = useState<BlockData[]>([]);
  const [localBlockItems, setLocalBlockItems] = useState<BlockData[]>(blockItems);

  const handleUpdate = useCallback(() => {
    Update(pageId, JSON.stringify(blockItems), () => handleSave(false));
  }, [Update, blockItems, handleSave, pageId]);

  const renderEditButton = () => {
    if (isEditing) {
      return (
        <Button
          style={{ fontWeight: '700', border: 'none', outline: 'none' }}
          className="btn btn-back-page"
          startIcon={<ArrowBackIosNewIcon />}
          onClick={() => { handleEdit(!isEditing); }}
          disabled={isSaving}
        >
          Preview
        </Button>
      );
    }
    return (
      <Button
        style={{ fontWeight: '300', border: '1px solid #6c757d' }}
        className="btn"
        startIcon={<FontAwesomeIcon style={{ fontSize: '14px' }} icon={faPenToSquare} />}
        onClick={() => { handleEdit(!isEditing); }}
      >
        Edit
      </Button>
    );
  };

  useEffect(() => {
    if (!isMobile) {
      setAnchorEl(null);
    }
  }, [isMobile]);

  useEffect(() => {
    if (contextErrors) {
      if ((typeof contextErrors === 'object' && Object.keys(contextErrors).length > 0) || contextErrors.length > 0) {
        setOpenPageSettingModal(true);
      }
    }
  }, [contextErrors]);

  useEffect(() => {
    setLocalInitialBlockItems(initialBlockItems);
  }, [initialBlockItems]);

  useEffect(() => {
    setLocalBlockItems(blockItems);
  }, [blockItems]);

  const handleUpdateConfirm = () => {
    if (isSaving) {
      return;
    }
    // eslint-disable-next-line no-alert
    if (confirm('Do you want to save?')) {
      handleSave(!isSaving);
      handleUpdate();
    }
  };

  const handleRemoveLayout = () => {
    const remove = () => {
      removeLayout();
      navigate('/me');
    };

    if (JSON.stringify(localBlockItems) !== JSON.stringify(localInitialBlockItems)) {
      if (window.confirm('Are you sure you want to leave this site?\nYour changes may not be saved.')) remove();
      return;
    }

    remove();
  };

  return (
    <Box sx={{ width: '100%' }}>
      {(!isMobile) ? (
        <Grid container justifyContent="space-between" wrap="nowrap">
          <Grid item container xs={8} justifyContent="start" alignItems="center" sx={{ width: '100%' }}>
            {!isEditing && (
              <a>
                <Button
                  className="btn btn-outline-secondary btn-back-page"
                  style={{ fontWeight: 700, border: 'none' }}
                  startIcon={<ArrowBackIosNewIcon fontSize="small" />}
                  onClick={handleRemoveLayout}
                >
                  My Page
                </Button>
              </a>
            )}
            <Box sx={{ paddingLeft: isEditing ? 0 : 1 }}>
              {renderEditButton()}
            </Box>
            {isEditing && (
              <>
                <Box sx={{ paddingLeft: 1, display: 'flex' }}>
                  <Box sx={{ paddingLeft: 0.5 }}>
                    <Button
                      startIcon={<SaveOutlined fontSize="small" />}
                      className="btn btn-outline-secondary"
                      onClick={handleUpdateConfirm}
                      disabled={isSaving}
                    >
                      Save
                    </Button>
                  </Box>
                  <Box sx={{ paddingLeft: 0.5 }}>
                    <Button
                      sx={{
                        minWidth: 'auto',
                        width: 36,
                        paddingLeft: '0px !important',
                        paddingRight: '0px !important',
                        '& .MuiButton-startIcon': { marginX: 'auto', width: 20 },
                      }}
                      startIcon={<ReplayRounded fontSize="small" />}
                      className="btn btn-outline-secondary"
                      onClick={() => { if (!isSaving) handleUndo(); }}
                      disabled={!canUndo}
                    />
                  </Box>
                </Box>
              </>
            )}
            <Box sx={{ display: 'flex', paddingLeft: 0.5 }}>
              {/* {!isEditing && (
                <Box sx={{ paddingRight: 0.5 }}>
                  {renderHtmlExportButton()}
                </Box>
              )} */}
              {renderSmartPhoneButton()}
            </Box>
          </Grid>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                marginRight: 1,
                border: isEditing ? '3px solid #dc3545' : '3px solid #1976d2',
                padding: '0 0.5rem',
                fontWeight: 700,
              }}
              color={isEditing ? '#dc3545' : '#1976d2'}
            >
              {!isEditing ? 'Preview' : 'Editing'}
            </Typography>
            <Box sx={{ paddingLeft: 0.5 }}>
              {isEditing && (
                <Button
                  sx={{ fontWeight: 700 }}
                  disabled={isSaving}
                  onClick={() => { setOpenPageSettingModal(true); }}
                  startIcon={<SettingsRounded fontSize="small" />}
                  variant="text"
                  color="inherit"
                >
                  Page Settings
                </Button>
              )}
            </Box>
            <MyMenu hasMainPage hasMenu={false} name={`${user?.surname ?? ''} ${user?.name}` || ''} planInitial={user?.myPlan?.initial} planName={user?.myPlan?.name} />
          </Box>
        </Grid>
      ) : (
        <Grid container justifyContent="start" wrap="nowrap" sx={{ height: 36.5 }}>
          <Grid item container alignItems="center">
            <a className="sidebar-toggle js-sidebar-toggle" style={{ cursor: 'default' }}>
              {open ? <FontAwesomeIcon style={{ cursor: 'pointer' }} icon={faX} onClick={() => setAnchorEl(null)} />
                : <FontAwesomeIcon style={{ cursor: 'pointer' }} icon={faBars} onClick={(event) => setAnchorEl(event.currentTarget)} />}
            </a>
            <Menu
              id="hamburger-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={() => setAnchorEl(null)}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
            >
              <Grid container direction="column" sx={{ px: 1 }}>
                {!isEditing && (
                  <Box sx={{ paddingBottom: 1 }}>
                    <Link to="/me">
                      <Button variant="outlined" onClick={() => { removeLayout(); }}>
                        To my page
                      </Button>
                    </Link>
                  </Box>
                )}
                <Box>
                  {renderEditButton()}
                </Box>
                {isEditing && (
                  <>
                    <Box sx={{ py: 1 }}>
                      <Button variant="outlined" onClick={handleUpdateConfirm} disabled={isSaving}>Save</Button>
                    </Box>
                    <Box sx={{ paddingBottom: 1 }}>
                      <Button disabled={!canUndo} variant="outlined" onClick={handleUndo}>
                        <Undo />
                      </Button>
                    </Box>
                    <Box>
                      <Button sx={{ fontWeight: 700 }} onClick={() => { setOpenPageSettingModal(true); }} startIcon={<SettingsRounded fontSize="small" />} variant="text" color="inherit">
                        Page settings
                      </Button>
                    </Box>
                  </>
                )}

              </Grid>
            </Menu>
          </Grid>

          <Grid item container justifyContent="end" alignItems="center">
            <Box sx={{ paddingLeft: 1, width: 120 }}>
              <Typography variant="body2" sx={{ textAlign: 'center' }} color={isEditing ? 'red' : '#1976d2'}>
                {!isEditing ? 'Preview' : 'Editing'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      )}

      <ModalComponent
        centered
        size="lg"
        backdrop="static"
        title="Input error"
        cancelButtonText="Close"
        hideConfirmButton
        hideHeaderCloseButton
        show={validationErrors.length > 0}
        closeModal={() => setValidationErrors([])}
      >
        <Box sx={{ padding: 2 }}>
          <Typography variant="body2" color="error">
            The following errors exist. Please fix them.
          </Typography>
          <ul>
            {validationErrors.map((error) => (
              <li key={error}>
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
              </li>
            ))}
          </ul>
        </Box>
      </ModalComponent>

      <PageSettingsModal
        open={openPageSettingModal}
        loading={updatePageLoading}
        settings={settings}
        handleClose={() => setOpenPageSettingModal(false)}
        handleSavePageSetting={(settings) => {
          const validationErrors = sanitizePageSettingsForm(settings);
          setValidationErrors(validationErrors);

          if (validationErrors.length > 0) return;
          handleSettingSave(settings, () => {
            setOpenPageSettingModal(false);
          });
        }}
        pageId={pageId}
      />
    </Box>
  );
};

export default MainPageLayout;
